import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { JobTray } from "@/components/JobTray";
import AppShell from "@/components/layout/AppShell";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Profile from "@/pages/Profile";
import Hris from "@/pages/Hris";
import HrisDetail from "@/pages/HrisDetail";
import ScheduleTypes from "@/pages/ScheduleTypes";
import Sites from "@/pages/Sites";
import DtrReport from "@/pages/DtrReport";
import { FinanceDashboard, ChartOfAccounts, JournalEntries, AccountsPayable, AccountsReceivable, Budgets } from "@/pages/Finance";
import Leave from "@/pages/Leave";
import Payroll from "@/pages/Payroll";
import Tasks from "@/pages/Tasks";
import Notifications from "@/pages/Notifications";
import Settings from "@/pages/Settings";
import Procurement from "@/pages/Procurement";
import SdDashboard from "@/pages/servicedesk/SdDashboard";
import SdSubmit from "@/pages/servicedesk/SdSubmit";
import SdTickets from "@/pages/servicedesk/SdTickets";
import SdTicketDetail from "@/pages/servicedesk/SdTicketDetail";
import SdKb from "@/pages/servicedesk/SdKb";
import SdAudit from "@/pages/servicedesk/SdAudit";
import SdSettings from "@/pages/servicedesk/SdSettings";
import SdTeams from "@/pages/servicedesk/SdTeams";
import { AmsShell } from "@/pages/ams/kit";
import AmsDashboard from "@/pages/ams/AmsDashboard";
import AmsInventory from "@/pages/ams/AmsInventory";
import AmsApprovals from "@/pages/ams/AmsApprovals";
import AmsWorkflows from "@/pages/ams/AmsWorkflows";
import AmsQr from "@/pages/ams/AmsQr";
import AmsFinance from "@/pages/ams/AmsFinance";
import AmsDisposal from "@/pages/ams/AmsDisposal";
import { AmsReports, AmsCompliance, AmsAudit, AmsMassUpload, AmsPolicies } from "@/pages/ams/AmsMore";
import AmsSettings from "@/pages/ams/AmsSettings";
import CustodyConfirm from "@/pages/CustodyConfirm";
import DmsHome from "@/pages/dms/DmsHome";
import CopyLink from "@/pages/dms/CopyLink";
import Tenants from "@/pages/Tenants";
import ModuleSettings from "@/pages/ModuleSettings";
import HrisPhase from "@/pages/HrisPhase";
import RequestForm from "@/pages/hris/RequestForm";
import ApprovalQueue from "@/pages/hris/ApprovalQueue";
import Masterfile from "@/pages/hris/Masterfile";
import Attendances from "@/pages/hris/Attendances";
import Timekeeping from "@/pages/hris/Timekeeping";
import AttendanceComputation from "@/pages/hris/AttendanceComputation";
import PayrollMaster from "@/pages/hris/PayrollMaster";
import HrisSettings from "@/pages/hris/settings/HrisSettings";
import PayrollMemo from "@/pages/hris/PayrollMemo";
import BankUpload from "@/pages/hris/BankUpload";
import EmployeeComp from "@/pages/hris/EmployeeComp";
import LeaveBalances from "@/pages/hris/LeaveBalances";
import HrisReports from "@/pages/hris/HrisReports";
import Handbook from "@/pages/hris/Handbook";
import ClockSettings from "@/pages/hris/ClockSettings";
import SharedClock from "@/pages/hris/SharedClock";
import HrisCompanies from "@/pages/hris/Companies";
import Chat from "@/pages/Chat";
import Attendance from "@/pages/Attendance";
import EsdApp from "@/esd/EsdApp";
import EsdPublicTracking from "@/esd/pages/PublicTracking";
import Track from "@/pages/Track";
import ForceChangePassword from "@/pages/ForceChangePassword";

function Protected({ children, perm }) {
  const { user, hasPerm } = useAuth();
  if (user === null)
    return (
      <div className="h-screen flex items-center justify-center text-slate-400">
        <div className="animate-pulse">Loading UEMP…</div>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForceChangePassword />;
  if (perm && !hasPerm(perm))
    return (
      <AppShell>
        <div className="uemp-fade-in" data-testid="access-denied">
          <div className="rounded-xl border border-dashed border-rose-200 bg-white p-12 text-center max-w-lg mx-auto mt-10">
            <div className="h-14 w-14 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto mb-4 text-2xl font-bold">!</div>
            <div className="text-slate-800 font-medium">Access denied</div>
            <p className="text-sm text-slate-500 mt-2">You don't have permission to view this page. Contact your administrator if you believe this is a mistake.</p>
          </div>
        </div>
      </AppShell>
    );
  return <AppShell>{children}</AppShell>;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/track/:token" element={<Track />} />
            <Route path="/esd-track/:token" element={<EsdPublicTracking />} />
            <Route path="/custody/:token" element={<CustodyConfirm />} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/profile" element={<Protected><Profile /></Protected>} />
            <Route path="/hris" element={<Protected><Hris /></Protected>} />
            <Route path="/hris/dtr" element={<Protected perm="employee.edit"><DtrReport /></Protected>} />
            <Route path="/hris/schedule-types" element={<Protected perm="employee.edit"><ScheduleTypes /></Protected>} />
            <Route path="/hris/sites" element={<Protected perm="employee.edit"><Sites /></Protected>} />
            <Route path="/hris/attendances" element={<Protected><Attendances /></Protected>} />
            <Route path="/hris/forms/wfh" element={<Protected><RequestForm type="wfh" /></Protected>} />
            <Route path="/hris/forms/overtime" element={<Protected><RequestForm type="overtime" /></Protected>} />
            <Route path="/hris/forms/official-business" element={<Protected><RequestForm type="official_business" /></Protected>} />
            <Route path="/hris/forms/dtr-correction" element={<Protected><RequestForm type="dtr_correction" /></Protected>} />
            <Route path="/hris/approvals/leave" element={<Protected><ApprovalQueue type="leave" /></Protected>} />
            <Route path="/hris/approvals/overtime" element={<Protected><ApprovalQueue type="overtime" /></Protected>} />
            <Route path="/hris/approvals/wfh" element={<Protected><ApprovalQueue type="wfh" /></Protected>} />
            <Route path="/hris/approvals/official-business" element={<Protected><ApprovalQueue type="official_business" /></Protected>} />
            <Route path="/hris/approvals/dtr-correction" element={<Protected><ApprovalQueue type="dtr_correction" /></Protected>} />
            <Route path="/hris/timekeeping" element={<Protected perm="employee.edit"><Timekeeping /></Protected>} />
            <Route path="/hris/payroll/attendance-computation" element={<Protected perm="employee.edit"><AttendanceComputation /></Protected>} />
            <Route path="/hris/payroll/master" element={<Protected perm="employee.edit"><PayrollMaster /></Protected>} />
            <Route path="/hris/payroll/memo" element={<Protected><PayrollMemo /></Protected>} />
            <Route path="/hris/payroll/bank-upload" element={<Protected perm="employee.edit"><BankUpload /></Protected>} />
            <Route path="/hris/settings/:tab?" element={<Protected perm="employee.edit"><HrisSettings /></Protected>} />
            <Route path="/hris/config/holidays" element={<Protected perm="employee.edit"><Masterfile entity="holidays" /></Protected>} />
            <Route path="/hris/config/leave-types" element={<Protected perm="employee.edit"><Masterfile entity="leave-types" /></Protected>} />
            <Route path="/hris/config/handbook" element={<Protected perm="employee.edit"><Handbook /></Protected>} />
            <Route path="/hris/config/clock-settings" element={<Protected perm="employee.edit"><ClockSettings /></Protected>} />
            <Route path="/hris/masterfiles/companies" element={<Protected perm="employee.edit"><HrisCompanies /></Protected>} />
            <Route path="/hris/masterfiles/departments" element={<Protected perm="employee.edit"><Masterfile entity="departments" /></Protected>} />
            <Route path="/hris/masterfiles/employee-levels" element={<Protected perm="employee.edit"><Masterfile entity="employee-levels" /></Protected>} />
            <Route path="/hris/masterfiles/early-cutoff" element={<Protected perm="employee.edit"><Masterfile entity="early-cutoff" /></Protected>} />
            <Route path="/hris/masterfiles/cost-center" element={<Protected perm="employee.edit"><Masterfile entity="cost-center" /></Protected>} />
            <Route path="/hris/masterfiles/projects" element={<Protected perm="employee.edit"><Masterfile entity="projects" /></Protected>} />
            <Route path="/hris/masterfiles/employee-allowances" element={<Protected perm="employee.edit"><EmployeeComp kind="allowances" /></Protected>} />
            <Route path="/hris/masterfiles/employee-deductions" element={<Protected perm="employee.edit"><EmployeeComp kind="deductions" /></Protected>} />
            <Route path="/hris/masterfiles/leave-balances" element={<Protected perm="employee.edit"><LeaveBalances /></Protected>} />
            <Route path="/hris/reports/:kind" element={<Protected perm="employee.edit"><HrisReports /></Protected>} />
            <Route path="/hris/:id" element={<Protected><HrisDetail /></Protected>} />
            <Route path="/finance" element={<Protected><FinanceDashboard /></Protected>} />
            <Route path="/finance/accounts" element={<Protected><ChartOfAccounts /></Protected>} />
            <Route path="/finance/journals" element={<Protected><JournalEntries /></Protected>} />
            <Route path="/finance/payable" element={<Protected><AccountsPayable /></Protected>} />
            <Route path="/finance/receivable" element={<Protected><AccountsReceivable /></Protected>} />
            <Route path="/finance/budgets" element={<Protected><Budgets /></Protected>} />
            <Route path="/leave" element={<Protected><Leave /></Protected>} />
            <Route path="/payroll" element={<Protected><Payroll /></Protected>} />
            <Route path="/tasks" element={<Protected><Tasks /></Protected>} />
            <Route path="/tenants" element={<Protected><Tenants /></Protected>} />
            <Route path="/chat" element={<Protected><Chat /></Protected>} />
            <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
            <Route path="/attendance" element={<Protected><Attendance /></Protected>} />
            <Route path="/hris/shared-clock" element={<Protected><SharedClock /></Protected>} />
            <Route path="/procurement" element={<Protected><Procurement /></Protected>} />
            <Route path="/itam" element={<Navigate to="/ams" replace />} />
            <Route path="/itam/inventory" element={<Navigate to="/ams/inventory" replace />} />
            <Route path="/itam/audit" element={<Navigate to="/ams/audit" replace />} />
            <Route path="/itam/compliance" element={<Navigate to="/ams/compliance" replace />} />
            <Route path="/itam/workflows" element={<Navigate to="/ams/onboarding" replace />} />
            <Route path="/itam/tickets" element={<Navigate to="/servicedesk/my" replace />} />
            <Route path="/servicedesk" element={<Protected><SdDashboard /></Protected>} />
            <Route path="/servicedesk/new" element={<Protected><SdSubmit /></Protected>} />
            <Route path="/servicedesk/my" element={<Protected><SdTickets /></Protected>} />
            <Route path="/servicedesk/approvals" element={<Protected><SdTickets /></Protected>} />
            <Route path="/servicedesk/queue" element={<Protected><SdTickets /></Protected>} />
            <Route path="/servicedesk/tickets/:id" element={<Protected><SdTicketDetail /></Protected>} />
            <Route path="/servicedesk/kb" element={<Protected><SdKb /></Protected>} />
            <Route path="/servicedesk/audit" element={<Protected><SdAudit /></Protected>} />
            <Route path="/servicedesk/settings" element={<Protected><SdSettings /></Protected>} />
            <Route path="/servicedesk/teams" element={<Protected><SdTeams /></Protected>} />
            <Route path="/assets" element={<Navigate to="/ams/inventory" replace />} />
            <Route path="/ams" element={<Protected><AmsShell><AmsDashboard /></AmsShell></Protected>} />
            <Route path="/ams/inventory" element={<Protected><AmsShell><AmsInventory /></AmsShell></Protected>} />
            <Route path="/ams/approvals" element={<Protected><AmsShell><AmsApprovals /></AmsShell></Protected>} />
            <Route path="/ams/onboarding" element={<Protected><AmsShell><AmsWorkflows key="on" kind="onboarding" /></AmsShell></Protected>} />
            <Route path="/ams/offboarding" element={<Protected><AmsShell><AmsWorkflows key="off" kind="offboarding" /></AmsShell></Protected>} />
            <Route path="/ams/qr" element={<Protected><AmsShell><AmsQr /></AmsShell></Protected>} />
            <Route path="/ams/finance" element={<Protected><AmsShell><AmsFinance /></AmsShell></Protected>} />
            <Route path="/ams/disposal" element={<Protected><AmsShell><AmsDisposal /></AmsShell></Protected>} />
            <Route path="/ams/reports" element={<Protected><AmsShell><AmsReports /></AmsShell></Protected>} />
            <Route path="/ams/compliance" element={<Protected><AmsShell><AmsCompliance /></AmsShell></Protected>} />
            <Route path="/ams/audit" element={<Protected><AmsShell><AmsAudit /></AmsShell></Protected>} />
            <Route path="/ams/mass-upload" element={<Protected><AmsShell><AmsMassUpload /></AmsShell></Protected>} />
            <Route path="/ams/policies" element={<Protected><AmsShell><AmsPolicies /></AmsShell></Protected>} />
            <Route path="/ams/settings" element={<Protected><AmsShell><AmsSettings /></AmsShell></Protected>} />
            <Route path="/ams/settings/:tab" element={<Protected><AmsShell><AmsSettings /></AmsShell></Protected>} />
            <Route path="/documents" element={<Protected><DmsHome /></Protected>} />
            <Route path="/documents/link/:token" element={<Protected><CopyLink /></Protected>} />
            <Route path="/esd/*" element={<Protected><EsdApp /></Protected>} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />
            <Route path="/module/:moduleId/settings" element={<Protected><ModuleSettings /></Protected>} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        <JobTray />
      </AuthProvider>
    </div>
  );
}

export default App;
